/**
 * 버전 비교를 위한 파라미터 인터페이스
 */
export interface VersionComparisonParams {
  /** 현재 버전 (예: "1.2.3") */
  currentVersion: string;
  /** 필요한 최소 버전 (예: "1.2.0") */
  requiredVersion: string;
}

/**
 * 버전 비교를 위한 타입
 * @param params - 버전 비교 파라미터
 * @returns currentVersion이 requiredVersion보다 크거나 같으면 true, 작으면 false
 * @throws Error 버전 형식이 잘못되었거나 필수 파라미터가 누락된 경우
 */
export type VersionComparison = (params: VersionComparisonParams) => boolean;

/**
 * 버전 문자열을 숫자 배열로 변환하는 타입
 * @param version - 변환할 버전 문자열 (예: "1.2.3")
 * @returns 버전을 구성하는 숫자 배열 (예: [1, 2, 3])
 */
export type ParseVersion = (version: string) => number[];

/**
 * 버전 비교를 위한 유틸리티 함수들
 */
export interface VersionUtils {
  /**
   * 현재 버전이 필요한 최소 버전을 만족하는지 확인하는 함수
   * @param params - 버전 비교 파라미터
   * @returns currentVersion이 requiredVersion보다 크거나 같으면 true, 작으면 false
   * @throws Error 버전 형식이 잘못되었거나 필수 파라미터가 누락된 경우
   */
  isVersionSatisfied: VersionComparison;

  /**
   * 버전 문자열을 숫자 배열로 변환하는 함수
   * @param version - 변환할 버전 문자열 (예: "1.2.3")
   * @returns 버전을 구성하는 숫자 배열 (예: [1, 2, 3])
   */
  parseVersion: ParseVersion;
}

/**
 * 버전 문자열이 유효한지 검증하는 함수
 * @param version - 검증할 버전 문자열
 * @returns 버전이 유효하면 true, 아니면 false
 */
const validateVersion = (version: string): boolean => {
  if (typeof version !== 'string') {
    console.warn('버전은 문자열이어야 합니다.');
    return false;
  }

  if (!version) {
    console.warn('버전 문자열은 필수입니다.');
    return false;
  }

  if (!/^\d+(?:\.\d+)*$/.test(version)) {
    console.warn('버전 형식이 올바르지 않습니다. (예: "1.2.3")');
    return false;
  }

  return true;
};

/**
 * 버전 문자열을 숫자 배열로 변환하는 함수
 * @param version - 변환할 버전 문자열 (예: "1.2.3")
 * @returns 버전을 구성하는 숫자 배열 (예: [1, 2, 3])
 */
const parseVersion: ParseVersion = (version) => {
  if (!validateVersion(version)) {
    return [];
  }
  return version.split('.').map(Number);
};

/**
 * 현재 버전이 필요한 최소 버전을 만족하는지 확인하는 함수
 * @param params - 버전 비교 파라미터
 * @returns currentVersion이 requiredVersion보다 크거나 같으면 true, 작으면 false
 * @throws Error 버전 형식이 잘못되었거나 필수 파라미터가 누락된 경우
 */
const isVersionSatisfied: VersionComparison = ({ currentVersion, requiredVersion }) => {
  const currentVersionNumbers = parseVersion(currentVersion);
  const requiredVersionNumbers = parseVersion(requiredVersion);

  // 두 버전의 길이가 다를 경우, 짧은 쪽의 나머지 부분을 0으로 채움
  const maxLength = Math.max(currentVersionNumbers.length, requiredVersionNumbers.length);
  while (currentVersionNumbers.length < maxLength) currentVersionNumbers.push(0);
  while (requiredVersionNumbers.length < maxLength) requiredVersionNumbers.push(0);

  // 각 자릿수를 순차적으로 비교
  for (let i = 0; i < maxLength; i++) {
    if (currentVersionNumbers[i] > requiredVersionNumbers[i]) return true;
    if (currentVersionNumbers[i] < requiredVersionNumbers[i]) return false;
  }

  // 모든 자릿수가 같으면 true 반환
  return true;
};

export const versionUtils: VersionUtils = {
  isVersionSatisfied,
  parseVersion,
};
